import {
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
  DOReportParams,
  DOReportRecord,
} from "../../../@types/DRO/AllReportsResponse";
import { GetAllVendorsListApiResponse } from "../../../@types/DRO/GetAllVendorsResponse";
import { GetAllWarehouseApiResponse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { AllReportsApi } from "../../../api/AllReportsDownload/AllReportsApi";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";

const DOReport: React.FC = () => {
  // State for data and pagination
  const [data, setData] = useState<DOReportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // State for filters
  const [filters, setFilters] = useState<DOReportParams>({
    vendor_id: undefined, // use vendor_id, not vendor_name
    part_code: undefined,
    part_name: "",
    po_no: undefined,
    do_no: undefined,
    location: undefined,
    start_date: getDefaultDates().startDate,
    end_date: getDefaultDates().endDate,
    page: 1,
    page_size: 50,
  });

  // Vendor Autocomplete
  const [vendors, setVendors] = useState<
    GetAllVendorsListApiResponse["data"]["data"]
  >([]);
  const [selectedVendor, setSelectedVendor] = useState<
    GetAllVendorsListApiResponse["data"]["data"][0] | null
  >(null);
  const [vendorsCurrentPage, setVendorsCurrentPage] = useState(0);
  const [vendorsSearchQuery, setVendorsSearchQuery] = useState("");
  const [hasMoreVendors, setHasMoreVendors] = useState(true);

  // Warehouse Autocomplete
  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<
    GetAllWarehouseApiResponse["data"]["data"][0] | null
  >(null);
  const [warehousesCurrentPage, setWarehousesCurrentPage] = useState(0);
  const [warehousesSearchQuery, setWarehousesSearchQuery] = useState("");
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);

  const rowsPerPage = 10;

  // Fetch vendors (with infinite scroll & search)
  const fetchVendors = useCallback(async () => {
    try {
      const response = await GetAllVendorsListApi.api.getAllVendorsList(
        vendorsCurrentPage,
        rowsPerPage,
        vendorsSearchQuery
      );
      setVendors((prev) =>
        vendorsCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreVendors(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching vendors", error);
    }
  }, [vendorsCurrentPage, vendorsSearchQuery]);

  // Fetch warehouses (with infinite scroll & search)
  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        warehousesCurrentPage,
        rowsPerPage,
        warehousesSearchQuery
      );
      setWarehouses((prev) =>
        warehousesCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreWarehouses(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching warehouses", error);
    }
  }, [warehousesCurrentPage, warehousesSearchQuery]);

  // Handle scroll for infinite loading
  const handleScroll = (
    event: React.SyntheticEvent,
    type: "vendors" | "warehouses"
  ) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;

    if (listboxNode.scrollHeight - position <= 1) {
      switch (type) {
        case "vendors":
          if (hasMoreVendors) {
            setVendorsCurrentPage((prev) => prev + 1);
          }
          break;
        case "warehouses":
          if (hasMoreWarehouses) {
            setWarehousesCurrentPage((prev) => prev + 1);
          }
          break;
      }
    }
  };

  // Numeric input change
  const handleNumericInputChange =
    (field: "part_code" | "po_no" | "do_no") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === "" || /^[0-9]+$/.test(value)) {
        setFilters({
          ...filters,
          [field]: value === "" ? undefined : parseInt(value, 10),
        });
        setCurrentPage(1);
      }
    };

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        ...filters,
        vendor_name: selectedVendor?.id,
        location: selectedWarehouse?.id,
        page: currentPage,
        page_size: pageSize,
      };

      // Remove any filter keys that are empty or undefined
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await AllReportsApi.api.getDOReport(params);
      if (response.success) {
        setData(response.records);
        setTotalPages(response.total_pages);
      }
    } catch (err) {
      setError("Failed to fetch DO report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    currentPage,
    pageSize,
    selectedVendor,
    selectedWarehouse,
  ]);

  // Initial fetch for dropdowns
  useEffect(() => {
    const debouncedFetchVendors = debounce(fetchVendors, 500);
    debouncedFetchVendors();
    return () => debouncedFetchVendors.cancel();
  }, [fetchVendors, vendorsCurrentPage]);

  useEffect(() => {
    const debouncedFetchWarehouses = debounce(fetchWarehouses, 500);
    debouncedFetchWarehouses();
    return () => debouncedFetchWarehouses.cancel();
  }, [fetchWarehouses, warehousesCurrentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange =
    (field: keyof DOReportParams) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFilters({
        ...filters,
        [field]: event.target.value,
      });
      setCurrentPage(1);
    };

  // Handle search button click
  const handleSearch = () => {
    fetchData();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const defaultDates = getDefaultDates();
    setFilters({
      vendor_id: undefined,
      part_code: undefined,
      part_name: "",
      po_no: undefined,
      do_no: undefined,
      location: undefined,
      start_date: defaultDates.startDate,
      end_date: defaultDates.endDate,
    });
    setSelectedVendor(null);
    setSelectedWarehouse(null);
    setCurrentPage(1);
  };

  // Handle download
  const handleDownload = async () => {
    try {
      setLoading(true);
      const params: any = {
        ...filters,
        location: selectedWarehouse?.id,
      };
      delete params.page;
      delete params.page_size;
      await AllReportsApi.api.downloadDOReport(params);
    } catch (err) {
      setError("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "CREATED":
        return { bgcolor: "#e3f2fd", color: "#1976d2" };
      case "RECEIVED":
        return { bgcolor: "#e8f5e9", color: "#2e7d32" };
      case "PARTIAL":
        return { bgcolor: "#fff3e0", color: "#f57c00" };
      case "CANCELLED":
        return { bgcolor: "#ffebee", color: "#d32f2f" };
      default:
        return { bgcolor: "#f5f5f5", color: "#666" };
    }
  };

  // Calculate fulfillment percentage
  const calculateFulfillment = (received: number, ordered: number) => {
    if (ordered === 0) return 0;
    return Math.round((received / ordered) * 100);
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#000" }}>
              Delivery Order (DO) Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              Track delivery orders with vendor details and fulfillment status
            </Typography>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
              Filter Options
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}>
              {/* Vendor Autocomplete */}
              <Autocomplete
                fullWidth
                size="small"
                options={vendors}
                getOptionLabel={(option) => option.name}
                value={selectedVendor}
                onChange={(event, newValue) => {
                  setSelectedVendor(newValue);
                  setFilters({
                    ...filters,
                    vendor_id: newValue ? newValue.id : undefined,
                  });
                  setCurrentPage(1);
                }}
                onInputChange={(event, newInputValue) => {
                  setVendorsCurrentPage(0);
                  setVendorsSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (e) => handleScroll(e, "vendors"),
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <TextField
                label="Reference Code"
                value={filters.part_code || ""}
                onChange={handleFilterChange("part_code")}
                inputProps={{
                  inputMode: "text",
                }}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Part Name"
                value={filters.part_name}
                onChange={handleFilterChange("part_name")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="PO Number"
                value={filters.po_no || ""}
                onChange={handleNumericInputChange("po_no")}
                size="small"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="DO Number"
                value={filters.do_no || ""}
                onChange={handleNumericInputChange("do_no")}
                size="small"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <Autocomplete
                fullWidth
                size="small"
                options={warehouses}
                getOptionLabel={(option) => option.name}
                value={selectedWarehouse}
                onChange={(event, newValue) => {
                  setSelectedWarehouse(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setWarehousesCurrentPage(0);
                  setWarehousesSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Warehouse"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (e) => handleScroll(e, "warehouses"),
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <TextField
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={handleFilterChange("start_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={handleFilterChange("end_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  borderColor: "#000",
                  color: "#000",
                  "&:hover": {
                    borderColor: "#000",
                    bgcolor: "#f5f5f5",
                  },
                }}>
                Clear Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}>
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid #000",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                  },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#999",
                  },
                }}>
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, border: "1px solid #ff5252" }}
            onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress sx={{ color: "#000" }} />
                </Box>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>DO No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>PO No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Invoice No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Reference Code
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Source DO No.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Part Name
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Cost Price
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Quantity
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Fulfillment
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Warehouse
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Received
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Original Order Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={15} align="center" sx={{ py: 5 }}>
                            <Typography color="text.secondary">
                              No records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.map((row) => {
                          const fulfillment = calculateFulfillment(
                            row.quantity_received,
                            row.quantity_ordered
                          );
                          return (
                            <TableRow
                              key={row.do_no}
                              sx={{
                                "&:hover": { bgcolor: "#fafafa" },
                                "&:last-child td": { border: 0 },
                              }}>
                              <TableCell>{row.do_no}</TableCell>
                              <TableCell>{row.po_no}</TableCell>
                              <TableCell>
                                <Tooltip title={row.invoice_id || "-"}>
                                  <Typography
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}>
                                    {row.invoice_id || "-"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={row.vendor_name}>
                                  <Typography
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}>
                                    {row.vendor_name}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={row.reference_code}>
                                  <Typography
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}>
                                    {row.reference_code || "-"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={row.source_do_no}>
                                  <Typography
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}>
                                    {row.source_do_no || "-"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={row.part_name}>
                                  <Typography
                                    sx={{
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}>
                                    {row.part_name}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(row.cost_price)}
                              </TableCell>
                              <TableCell align="center">
                                <Box>
                                  <Typography variant="body2">
                                    {row.quantity_received} /{" "}
                                    {row.quantity_ordered}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={
                                      row.pending_quantity > 0
                                        ? "error"
                                        : "success.main"
                                    }>
                                    {row.pending_quantity > 0
                                      ? `${row.pending_quantity} pending`
                                      : "Complete"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ width: 100 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 0.5,
                                    }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                      {fulfillment}%
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={fulfillment}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: "#e0e0e0",
                                      "& .MuiLinearProgress-bar": {
                                        bgcolor:
                                          fulfillment === 100
                                            ? "#4caf50"
                                            : fulfillment >= 50
                                            ? "#ff9800"
                                            : "#f44336",
                                      },
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>{row.location}</TableCell>
                              <TableCell>
                                <Chip
                                  label={row.status}
                                  size="small"
                                  sx={getStatusColor(row.status)}
                                />
                              </TableCell>
                              <TableCell>{row.created_at}</TableCell>
                              <TableCell>{row.receiving_date}</TableCell>
                              <TableCell>{row.original_order_date}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderTop: "1px solid #e0e0e0",
                      bgcolor: "#fafafa",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2">Rows per page:</Typography>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e as any)}
                        size="small"
                        sx={{
                          minWidth: 80,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                        }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            bgcolor: "#000",
                            color: "#fff",
                            "&:hover": {
                              bgcolor: "#333",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DOReport;
